import React, { useState, useRef, useEffect } from 'react';
import { IoClose, IoRefresh } from 'react-icons/io5';
import { FaBolt, FaWrench, FaPaintRoller, FaHammer, FaSnowflake, FaBroom, FaQuestionCircle } from 'react-icons/fa';
import { getSolution } from '../services/chatbotService';
import BookingForm from './BookingForm';
import scoutBee from '../assets/scout-bee.png';
import './Chatbot.css';

// ─── Service categories ────────────────────────────────────────────────────
const CATEGORIES = [
    { label: 'Electrical', slug: 'electrician', icon: <FaBolt /> },
    { label: 'Plumbing', slug: 'plumber', icon: <FaWrench /> },
    { label: 'Carpentry', slug: 'carpenter', icon: <FaHammer /> },
    { label: 'Painting', slug: 'painter', icon: <FaPaintRoller /> },
    { label: 'AC / Appliance', slug: 'ac-appliance', icon: <FaSnowflake /> },
    { label: 'Cleaning', slug: 'cleaning', icon: <FaBroom /> },
    { label: 'Other', slug: 'other', icon: <FaQuestionCircle /> },
];

const MAX_ATTEMPTS = 3;

// ─── Component ────────────────────────────────────────────────────────────
const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isBeeFlying, setIsBeeFlying] = useState(true);

    // phase: idle | describing | thinking | awaiting_feedback | resolved | escalate
    const [phase, setPhase] = useState('idle');

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([]);
    const [attemptCount, setAttemptCount] = useState(0);
    const [problemDescription, setProblemDescription] = useState('');
    const [showBookingForm, setShowBookingForm] = useState(false);
    const [contextInput, setContextInput] = useState('');
    const [freeInput, setFreeInput] = useState('');
    // Full conversation history passed to Groq: [{role:'user'|'assistant', content}]
    const [chatHistory, setChatHistory] = useState([]);

    const messagesEndRef = useRef(null);

    // ── Input validation ─────────────────────────────────────────────────────
    const VAGUE_PATTERNS = /^(hi|hello|hey|yo|sup|hii|helo|hola|greetings|howdy|what'?s up|test|ok|okay|yes|no|thanks|thank you|lol|hmm|help)[\s!?.]*$/i;

    // Detects when user signals they can't / don't know how to do the suggested fix
    const CANNOT_DO_PATTERNS = /i (don'?t|do not|cant|can'?t|don'?t|dunno|have no idea|am unable|am not able|have no clue|have no knowledge) (know|understand|do|perform|handle|manage|fix)/i;
    const CANNOT_DO_SIMPLE = /(too (hard|difficult|complicated|complex)|no (idea|clue|knowledge)|i'?m lost|not sure how|don'?t know how|dont know how|can'?t do (it|this|that)|cant do (it|this|that)|i give up|beyond me)/i;

    const isVagueInput = (text) => {
        if (text.length < 8) return true;
        if (VAGUE_PATTERNS.test(text.trim())) return true;
        return false;
    };

    const isCannotDoInput = (text) => {
        const t = text.trim();
        return CANNOT_DO_PATTERNS.test(t) || CANNOT_DO_SIMPLE.test(t);
    };

    // Bee entrance rocket-landing animation
    useEffect(() => {
        const timer = setTimeout(() => setIsBeeFlying(false), 4000);
        return () => clearTimeout(timer);
    }, []);

    // Auto-scroll to latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, phase]);

    // Open Honey when hero button is clicked
    useEffect(() => {
        const handler = () => setIsOpen(true);
        window.addEventListener('skillhive:openChat', handler);
        return () => window.removeEventListener('skillhive:openChat', handler);
    }, []);

    // ── Helpers ─────────────────────────────────────────────────────────────
    const addMessage = (text, type = 'bot') => {
        setMessages(prev => [...prev, { text, type }]);
    };

    const resetChat = () => {
        setPhase('idle');
        setSelectedCategory(null);
        setMessages([]);
        setAttemptCount(0);
        setProblemDescription('');
        setInputValue('');
        setFreeInput('');
        setShowBookingForm(false);
        setChatHistory([]);
    };

    // ── Step 1: Category selected ────────────────────────────────────────────
    const CATEGORY_PROMPTS = {
        'Electrical': 'Describe your electrical issue, or ask for advice (e.g. "which bulb type saves more energy?").',
        'Plumbing': 'Describe your plumbing issue, or ask for advice (e.g. "what pipe sealant should I use?").',
        'Carpentry': 'Describe your carpentry issue, or ask for advice (e.g. "what wood is best for outdoor furniture?").',
        'Painting': 'Describe your painting issue, or ask for suggestions (e.g. "what color suits a small living room?").',
        'AC / Appliance': 'Describe your appliance issue, or ask for advice (e.g. "how often should I clean AC filters?").',
        'Cleaning': 'Describe your cleaning issue, or ask for advice (e.g. "what removes grease stains from fabric?", "how to clean window glass streaks?", "best way to clean glass tower panels?").',
        'Other': "Describe your issue or ask for advice — I'll do my best to help.",
    };

    const handleCategorySelect = (cat) => {
        setSelectedCategory(cat);
        addMessage(cat.label, 'user');
        const prompt = CATEGORY_PROMPTS[cat.label] || `Got it! Describe your ${cat.label.toLowerCase()} issue or ask for advice.`;
        // Seed conversation history with the category so the AI always knows what was selected
        setChatHistory([
            { role: 'user', content: `I need help with: ${cat.label}` },
            { role: 'assistant', content: prompt }
        ]);
        setTimeout(() => {
            addMessage(prompt, 'bot');
            setPhase('describing');
        }, 350);
    };

    // ── Step 2: Problem submitted ────────────────────────────────────────────
    const handleDescriptionSubmit = () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;

        if (isVagueInput(trimmed)) {
            addMessage(trimmed, 'user');
            setInputValue('');
            setTimeout(() => {
                addMessage(
                    `That doesn't look like a problem description. Please tell me what's actually wrong — for example: "my light switch sparks when I flip it" or "circuit breaker keeps tripping".`,
                    'bot'
                );
            }, 350);
            return;
        }

        setProblemDescription(trimmed);
        addMessage(trimmed, 'user');
        setInputValue('');
        // Pass chatHistory which already contains the category selection context
        fetchSolution(trimmed, 1, chatHistory);
    };

    // ── Step 3: Fetch AI solution ────────────────────────────────────────────
    // newUserMessage : the latest user text to append to history
    // attempt        : 1 | 2 | 3
    // currentHistory : [{role, content}] — history so far (WITHOUT newUserMessage)
    const fetchSolution = async (newUserMessage, attempt, currentHistory) => {
        setPhase('thinking');
        const updatedHistory = [
            ...currentHistory,
            { role: 'user', content: newUserMessage }
        ];
        try {
            const { solution, detectedCategory } = await getSolution({
                category: selectedCategory.label,
                attemptNumber: attempt,
                conversationHistory: updatedHistory,
            });

            // If the AI detected a different category than what was selected, update it
            if (detectedCategory) {
                const match = CATEGORIES.find(
                    c => c.label.toLowerCase() === detectedCategory.toLowerCase()
                );
                if (match && match.slug !== selectedCategory.slug) {
                    setSelectedCategory(match);
                    addMessage(
                        `I noticed your issue is actually about ${match.label} — I've updated the category so the right technician is matched if you decide to book one.`,
                        'bot'
                    );
                }
            }

            const newHistory = [...updatedHistory, { role: 'assistant', content: solution }];
            setChatHistory(newHistory);
            setAttemptCount(attempt);
            addMessage(solution, 'solution');
            setPhase('awaiting_feedback');
        } catch (err) {
            console.error('Chatbot AI error:', err);
            addMessage(
                'Sorry, I ran into an error. Please try again.',
                'bot'
            );
            setPhase('escalate');
        }
    };

    // ── Step 4a: Problem resolved ────────────────────────────────────────────
    const handleFeedbackYes = () => {
        addMessage('Yes, it worked! ✅', 'user');
        setTimeout(() => {
            addMessage(
                "Great! Glad I could help. Feel free to chat again whenever you need assistance.",
                'bot'
            );
            setPhase('resolved');
        }, 350);
    };

    // ── Step 4b: Problem not resolved ───────────────────────────────────────
    const handleFeedbackNo = () => {
        addMessage('No, still not fixed.', 'user');
        const next = attemptCount + 1;

        if (next > MAX_ATTEMPTS) {
            setTimeout(() => {
                addMessage(
                    "I've shared all my suggestions but it looks like you need more personalised help. A professional technician would be best at this point!",
                    'bot'
                );
                setPhase('escalate');
            }, 350);
        } else {
            setTimeout(() => {
                addMessage(
                    `Can you share any more details? This helps me give a better answer. Or just skip and I'll try a different approach.`,
                    'bot'
                );
                setContextInput('');
                setPhase('updating_context');
            }, 350);
        }
    };

    // ── Step 4c: User provides more context (optional) ───────────────────────
    const handleContextSubmit = (extra) => {
        // If the user signals they can't do the fix, offer booking right away
        if (extra && isCannotDoInput(extra)) {
            addMessage(extra, 'user');
            setTimeout(() => {
                addMessage(
                    "No worries at all! Some fixes are best left to the pros. Would you like to book a professional technician to take care of this for you?",
                    'bot'
                );
                setPhase('escalate');
            }, 350);
            return;
        }

        const next = attemptCount + 1;
        // If extra provided — send it as the new user turn so AI has full context
        // If skipped — explicitly ask for a different approach
        const nextUserMessage = extra
            ? extra
            : 'That did not work. Please suggest a completely different approach.';

        if (extra) addMessage(extra, 'user');

        setTimeout(() => {
            addMessage(`Let me try a different approach — here's another suggestion:`, 'bot');
            // Pass the full chatHistory (which already contains all previous turns)
            fetchSolution(nextUserMessage, next, chatHistory);
        }, 350);
    };

    // ── Free-text input during awaiting_feedback / resolved / escalate ────────
    const handleFreeInput = () => {
        const text = freeInput.trim();
        if (!text) return;
        setFreeInput('');
        addMessage(text, 'user');
        const next = attemptCount + 1;
        setTimeout(() => {
            addMessage(`Let me look into that…`, 'bot');
            fetchSolution(text, next > MAX_ATTEMPTS ? MAX_ATTEMPTS : next, chatHistory);
        }, 300);
    };

    // ── Direct escalation (user wants a pro without more attempts) ───────────
    const handleBookPro = () => {
        addMessage("I'd rather book a professional.", 'user');
        setTimeout(() => {
            addMessage(
                "That's perfectly fine! A professional technician will get it sorted quickly and safely.",
                'bot'
            );
            setPhase('escalate');
        }, 350);
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            {/* Honey launcher — bubble + bee button */}
            <div className={`scout-launcher ${isBeeFlying ? 'bee-entrance' : ''}`}>
                {!isOpen && !isBeeFlying && (
                    <div className="scout-bubble">
                        👋 Honey here to help!
                    </div>
                )}
                <button
                    className={`chatbot-toggle ${isOpen ? 'active' : ''}`}
                    onClick={() => setIsOpen(o => !o)}
                    aria-label="Chat with Honey"
                >
                    {isOpen
                        ? <IoClose />
                        : <img src={scoutBee} className="scout-bee-img" alt="Honey" />
                    }
                </button>
            </div>

            {/* Chat window */}
            <div className={`chatbot-container ${isOpen ? 'open' : ''}`}>

                {/* Header */}
                <div className="chatbot-header">
                    <div className="chatbot-header-inner">
                        <div className="chatbot-header-text">
                            <h4>Honey <img src={scoutBee} alt="Honey" style={{ width: '26px', height: 'auto', verticalAlign: 'middle' }} /></h4>
                            <p>Your SkillHive guide</p>
                        </div>
                        {phase !== 'idle' && (
                            <button className="chatbot-reset-btn" onClick={resetChat} title="Start over">
                                <IoRefresh />
                            </button>
                        )}
                    </div>
                </div>

                {/* Messages */}
                <div className="chatbot-messages">
                    {phase === 'idle' && messages.length === 0 && (
                        <div className="welcome-bubble">
                            <p>👋 Hi! I'm <strong>Honey</strong>, your SkillHive guide. I can help with home repair issues <strong>and</strong> give advice (colors, materials, products, etc.).</p>
                            <p>Pick a category to get started:</p>
                        </div>
                    )}

                    {messages.map((msg, i) => (
                        <div
                            key={i}
                            className={`message ${msg.type === 'user'
                                ? 'user'
                                : msg.type === 'solution'
                                    ? 'bot solution-msg'
                                    : 'bot'
                                }`}
                        >
                            {msg.type === 'solution' && (
                                <span className="solution-label">💡 Suggested Fix</span>
                            )}
                            {msg.text}
                        </div>
                    ))}

                    {phase === 'thinking' && (
                        <div className="message bot typing-indicator">
                            <span /><span /><span />
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Footer — changes per phase */}
                <div className="chatbot-footer">

                    {/* IDLE: Category picker */}
                    {phase === 'idle' && (
                        <div className="category-grid">
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat.slug}
                                    className="category-btn"
                                    onClick={() => handleCategorySelect(cat)}
                                >
                                    <span className="cat-icon">{cat.icon}</span>
                                    <span className="cat-label">{cat.label}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* DESCRIBING: Textarea + send */}
                    {phase === 'describing' && (
                        <div className="chatbot-input-area">
                            <textarea
                                className="chatbot-textarea"
                                placeholder="Describe your issue or ask for advice... (Shift+Enter for new line)"
                                value={inputValue}
                                rows={3}
                                onChange={e => setInputValue(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleDescriptionSubmit();
                                    }
                                }}
                            />
                            <button
                                className="send-btn"
                                onClick={handleDescriptionSubmit}
                                disabled={!inputValue.trim()}
                            >
                                Send
                            </button>
                        </div>
                    )}

                    {/* AWAITING FEEDBACK: Yes / No + free text input */}
                    {phase === 'awaiting_feedback' && (
                        <div className="feedback-panel">
                            <p className="feedback-question">Was this helpful?</p>
                            <div className="feedback-btns">
                                <button className="feedback-yes" onClick={handleFeedbackYes}>
                                    ✅ Yes, solved!
                                </button>
                                <button className="feedback-no" onClick={handleFeedbackNo}>
                                    🔄 Need more help
                                </button>
                            </div>

                            {/* Free-text reply box */}
                            <div className="feedback-freetext">
                                <div className="freetext-divider"><span>or type your reply</span></div>
                                <div className="freetext-row">
                                    <input
                                        type="text"
                                        className="freetext-input"
                                        placeholder="Ask a follow-up or describe what's happening…"
                                        value={freeInput}
                                        onChange={e => setFreeInput(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && freeInput.trim()) {
                                                e.preventDefault();
                                                handleFreeInput();
                                            }
                                        }}
                                    />
                                    <button
                                        className="freetext-send-btn"
                                        onClick={handleFreeInput}
                                        disabled={!freeInput.trim()}
                                        aria-label="Send"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
                                    </button>
                                </div>
                            </div>

                            <button className="book-pro-inline-btn" onClick={handleBookPro}>
                                📞 Book a Professional Instead
                            </button>
                        </div>
                    )}

                    {/* UPDATING CONTEXT: optional extra details before next attempt */}
                    {phase === 'updating_context' && (
                        <div className="chatbot-input-area">
                            <textarea
                                className="chatbot-textarea"
                                placeholder="Optional: describe what happened or add more details..."
                                value={contextInput}
                                rows={3}
                                onChange={e => setContextInput(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleContextSubmit(contextInput.trim());
                                    }
                                }}
                            />
                            <div className="context-btns">
                                <button
                                    className="send-btn"
                                    onClick={() => handleContextSubmit(contextInput.trim())}
                                >
                                    {contextInput.trim() ? 'Send & get fix' : 'Skip, try another fix'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* RESOLVED */}
                    {phase === 'resolved' && (
                        <div className="end-panel">
                            <p className="end-panel-text">🎉 Issue resolved!</p>
                            <div className="freetext-row" style={{ marginBottom: '8px' }}>
                                <input
                                    type="text"
                                    className="freetext-input"
                                    placeholder="Have another question? Ask away…"
                                    value={freeInput}
                                    onChange={e => setFreeInput(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && freeInput.trim()) {
                                            e.preventDefault();
                                            handleFreeInput();
                                        }
                                    }}
                                />
                                <button
                                    className="freetext-send-btn"
                                    onClick={handleFreeInput}
                                    disabled={!freeInput.trim()}
                                    aria-label="Send"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
                                </button>
                            </div>
                            <button className="new-chat-btn" onClick={resetChat}>
                                Start new chat
                            </button>
                        </div>
                    )}

                    {/* ESCALATE: Book a technician */}
                    {phase === 'escalate' && (
                        <div className="escalate-panel">
                            <p className="escalate-text">
                                Let a professional <strong>{selectedCategory?.label}</strong> technician handle this.
                            </p>
                            <button
                                className="book-tech-btn"
                                onClick={() => setShowBookingForm(true)}
                            >
                                🔧 Book a Technician
                            </button>

                            {/* Free-text to keep chatting */}
                            <div className="freetext-row">
                                <input
                                    type="text"
                                    className="freetext-input"
                                    placeholder="Still have a question? Type here…"
                                    value={freeInput}
                                    onChange={e => setFreeInput(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && freeInput.trim()) {
                                            e.preventDefault();
                                            handleFreeInput();
                                        }
                                    }}
                                />
                                <button
                                    className="freetext-send-btn"
                                    onClick={handleFreeInput}
                                    disabled={!freeInput.trim()}
                                    aria-label="Send"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
                                </button>
                            </div>

                            <button className="new-chat-btn secondary" onClick={resetChat}>
                                Start new chat
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Booking form modal — opened from escalation */}
            {showBookingForm && (
                <BookingForm
                    serviceName={selectedCategory?.label || 'General Service'}
                    workerData={null}
                    prefillDescription={
                        `Problem: ${problemDescription}\n\n` +
                        `AI attempted ${attemptCount} solution(s) but the issue was not resolved. ` +
                        `Please send a technician.`
                    }
                    onClose={() => setShowBookingForm(false)}
                />
            )}
        </>
    );
};

export default Chatbot;
